import { normalizeWidgetTheme, APIURL, cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getSymbolDropdown, getTraderSMCardHTML, getWidgetDataURI, apiGet, toggleWidgetTheme, isHeaderPinned, getHeaderPinStateClass, getHeaderPinButtonHTML, setupHeaderPin, getNoDataHTML, isEmptyWidgetData } from './../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from './../../types';

export class TraderverseCryptoCompany {
    private container: HTMLElement;
    private config: WidgetConfig;
    private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
    private shadowRoot: ShadowRoot;
    private currentData: any[] = [];
    private currentChart: any;
    private widgetResizeObserver: ResizeObserver | null = null;
    private interval: '1d' | '5d' | '1m' = '1m';
    private total_current_value_usd: string = '0.00$';
    private activeTab: 'market-share' | 'total' = 'market-share';
    private activeCoin: 'bitcoin' | 'ethereum' | 'all' = 'bitcoin';
    private widgetIndex: number = 0;

    constructor(config: WidgetConfig) {
        this.config = { ...this.getDefaultConfig(), ...config };
        this.config.theme = normalizeWidgetTheme(this.config.theme);
        this.container = this.resolveContainer(config.container);

        this.shadowRoot = (this.container as any).shadowRoot || this.container.attachShadow({ mode: 'closed' });

        this.init();

        this.widgetResizeObserver = new ResizeObserver(() => this.updateDisplay());
        setTimeout(() => {
            const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
            if (widget) this.widgetResizeObserver?.observe(widget);
        }, 0);
    }

    private fetchSymbols(): Array<{ value: string; label: string }> {
        return [
            { label: 'AAPL', value: 'AAPL' }, { label: 'MSFT', value: 'MSFT' },
            { label: 'GOOGL', value: 'GOOGL' }, { label: 'AMZN', value: 'AMZN' }
        ];
    }

    private getDefaultConfig(): Partial<WidgetConfig> {
        const symbols = this.fetchSymbols();
        return {
      headerPinned: true,
            symbol: symbols[0].value,
            theme: 'default-dark',
            width: '100%',
            height: '400px',
            autosize: true
        };
    }

    private resolveContainer(container: string | HTMLElement): HTMLElement {
        if (typeof container === 'string') {
            const el = document.getElementById(container) || document.querySelector(container);
            if (!el) throw new Error(`Container not found: ${container}`);
            return el as HTMLElement;
        }
        return container;
    }

    private async init(): Promise<void> {
        if (!this.config.symbol) this.config.symbol = this.fetchSymbols()[0].value;
        this.createWidget();
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (widget) widget.classList.add('loading');
        await this.attachStyles();
        await this.setupChart(this.config.symbol!);
        await this.injectChartLibrary();
        this.setupEventListeners();
        if (widget) widget.classList.remove('loading');
        this.updateDisplay();
        cancelSelectEvent(this.shadowRoot);
        this.config.onReady?.();
    }

    private createWidget(): void {
        let themeClass = 'defaultDarkMode';
        let dataTheme = 'default-dark';
        
        if (this.config.theme === 'white') {
            themeClass = 'whiteMode';
            dataTheme = 'white';
        } else if (this.config.theme === 'black') {
            themeClass = 'blackMode';
            dataTheme = 'black';
        } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}
        const tabStyles = `
        `;
        const widgetHtml = `
        ${tabStyles}
        <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width};height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-testing-id="crypto-company-widget-${this.widgetIndex}" data-theme="${dataTheme}">
            <div class="headTop">
                <h4 class="title crypto">${this.getTitleIconSvg()} Company</h4>
                <div class="actionCol">${getHeaderPinButtonHTML(this.config)}<i class="pi pi-ellipsis-v"></i></div>
            </div>
            <div class="inBody">
                <div class="optionsRow">
                    <div class="item tabsDv crypto-tabs">
                        <button class="tab-btn coin-btn active" data-coin="bitcoin" data-testing-id="coin-tab-bitcoin">Bitcoin</button>
                        <button class="tab-btn coin-btn" data-coin="ethereum" data-testing-id="coin-tab-ethereum">Ethereum</button>
                    </div>
                    <span></span>
                    <div class="item tabsDv view-tabs">
                        <button class="tab-btn active" data-tab="market-share" data-testing-id="view-tab-market-share">Market Share</button>
                        <button class="tab-btn" data-tab="total" data-testing-id="view-tab-total">Total</button>
                    </div>
                </div>
                <div class="chartDv cryptoCompany" id="portfolioAssetTypeContainer"></div>
            </div>
        </div>`;
        this.shadowRoot.innerHTML = widgetHtml;
    }

    private async attachStyles(): Promise<void> {
        try {
            const styleEl = await getCSSStyles();
            this.shadowRoot.appendChild(styleEl);
        } catch (err) {
            console.error('Failed to load CSS file:', err);
        }
    }

    private setupEventListeners(): void {
    setupHeaderPin(this.shadowRoot, this.config);
        const intervalDropdown = this.shadowRoot.querySelector('.interval-dropdown') as HTMLSelectElement;
        intervalDropdown?.addEventListener('change', e => {
            this.interval = (e.target as HTMLSelectElement).value as '1d' | '5d' | '1m';
            this.setupChart(this.config.symbol!);
        });
        if (intervalDropdown) intervalDropdown.value = this.interval || '5d';

        // View type tabs (market share/total)
        const viewTabButtons = this.shadowRoot.querySelectorAll('.view-tabs .tab-btn');
        viewTabButtons.forEach(btn => {
            btn.addEventListener('click', (e) => {
                const target = e.target as HTMLElement;
                const tab = target.getAttribute('data-tab') as 'market-share' | 'total';

                viewTabButtons.forEach(b => b.classList.remove('active'));
                target.classList.add('active');

                this.activeTab = tab;
                this.updateDisplay();
            });
        });

        // Crypto coin tabs
        const coinTabButtons = this.shadowRoot.querySelectorAll('.crypto-tabs .tab-btn');
        coinTabButtons.forEach(btn => {
            btn.addEventListener('click', (e) => {
                const target = e.target as HTMLElement;
                const coin = target.getAttribute('data-coin') as 'bitcoin' | 'ethereum' | 'all';

                coinTabButtons.forEach(b => b.classList.remove('active'));
                target.classList.add('active');

                this.activeCoin = coin;
                this.setupChart(this.config.symbol!);
            });
        });
    }

    private async injectChartLibrary(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            if ((window as any).echarts) return resolve(true);
            const script = document.createElement('script');
            script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
            script.async = true;
            script.onload = () => resolve(true);
            this.shadowRoot.appendChild(script);
        });
    }

    private async setupChart(symbol: string): Promise<void> {
        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        try {
            const res = await apiGet({
                method: 'GET',
                url: `${APIURL}/v1/crypto/crypto_company_treasury`,
                headers: { 'Content-Type': 'application/json' }
            });
            if (!res.ok) throw new Error('Failed to fetch crypto company treasury data');
            const json = await res.json();

            // Filter data based on selected coin
            const filteredData = this.activeCoin === 'all'
                ? json.data.companyGroup
                : json.data.companyGroup.filter((item: any) =>
                    (item.coin_id || '').toLowerCase() === this.activeCoin
                );

            if (!filteredData || filteredData.length === 0) {
                widgetBox?.removeAttribute('data-testing-id');
                this.currentData = [];
                this.updateDisplay();
                return;
            }

            this.currentData = filteredData.map((item: any) => ({
                name: item.name,
                value: parseFloat(item.total_current_value_usd || '0'),
                displayValue: this.formatUSD(parseFloat(item.total_current_value_usd || '0')),
                itemStyle: { color: item.color || undefined }
            }));

            const totalValue = json.data.companyGroup.reduce(
                (sum: number, item: any) => sum + parseFloat(item.total_current_value_usd || '0'),
                0
            );

            this.total_current_value_usd = this.formatUSD(totalValue);
            this.updateDisplay();
        } catch (err) {
            console.error('Failed to fetch/setup chart data:', err);
            widgetBox?.removeAttribute('data-testing-id');
            this.currentData = [];
            this.updateDisplay();
        }
    }

    private updateDisplay(): void {
        const container = this.shadowRoot.querySelector('#portfolioAssetTypeContainer') as HTMLElement;

    const hasData = Array.isArray(this.currentData) && this.currentData.length > 0;

    if (hasData) {
        container?.setAttribute('data-testing-id', 'crypto-company-chart');
    } else {
        container?.removeAttribute('data-testing-id');
    }

    if (!(window as any).echarts) {
        setTimeout(() => this.updateDisplay(), 100);
        return;
    }

        if (isEmptyWidgetData(this.currentData)) {
            if (container) container.innerHTML = getNoDataHTML();
            return;
        }
        this.renderPortfolioChart();
    }

    private renderPortfolioChart(): void {
        const container = this.shadowRoot.querySelector('#portfolioAssetTypeContainer');
        if (!container) return;

        container.innerHTML = `<div id="portfolioChart" class="chart" style="height: 340px; min-width: 500px;"></div>`;
        this.drawChart();
    }

    private formatUSD(value: number): string {
        if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(2)}B`;
        if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`;
        if (value >= 1_000) return `$${(value / 1_000).toFixed(2)}K`;
        return `$${value.toFixed(2)}`;
    }

    /** Theme tokens from `.widgetBox` CSS vars — aligned with IPO Price Distribution and other charts. */
    private getChartThemeVars(widgetBox: HTMLElement): {
        textColor: string;
        axisLineColor: string;
        splitLineColor: string;
        fontSize: string;
        bgColor: string;
        bgPrimary: string;
    } {
        const cs = getComputedStyle(widgetBox);
        return {
            textColor: cs.getPropertyValue('--charts-text-color').trim(),
            axisLineColor: cs.getPropertyValue('--charts-axis-color').trim(),
            splitLineColor: cs.getPropertyValue('--charts-split-line-color').trim(),
            fontSize: cs.getPropertyValue('--font-size-sm').trim(),
            bgColor: cs.getPropertyValue('--base-dark').trim(),
            bgPrimary: cs.getPropertyValue('--bg-primary').trim(),
        };
    }

    private drawChart(): void {
        const echarts = (window as any).echarts;
        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        const container = this.shadowRoot.querySelector('#portfolioChart') as HTMLElement;
        if (!container || !this.currentData || !widgetBox) return;

        const instance = echarts.getInstanceByDom(container);
        if (instance) echarts.dispose(instance);

        const theme = this.getChartThemeVars(widgetBox);
        const textContentColor = getComputedStyle(widgetBox).getPropertyValue('--text-content').trim() || theme.textColor;

        const option = {
            backgroundColor: 'transparent',
            tooltip: {
                trigger: 'item',
                backgroundColor: theme.bgColor,
                borderColor: theme.axisLineColor,
                textStyle: { color: theme.textColor, fontSize: theme.fontSize },
                formatter: (params: any) => {
                    if (this.activeTab === 'total') {
                        return `${params.name}: ${this.formatUSD(Number(params.value) || 0)}`;
                    }
                    const percent = Number(params.percent ?? 0).toFixed(2);
                    return `${params.name}: ${percent}%`;
                }
            },
            legend: {
                orient: 'vertical',
                type: 'scroll',
                right: 20,
                top: 'center',
                width: 200,
                itemWidth: 14,
                itemHeight: 14,
                icon: "circle",
                textStyle: {
                    color: theme.textColor,
                    fontSize: theme.fontSize,
                    rich: {
                        name: {
                            fontFamily: 'Inter',
                            fontSize: theme.fontSize,
                            color: theme.textColor,
                            width: 145,           // required for truncate
                            overflow: 'truncate', // required for truncate
                            ellipsis: '...'       // required to show dots
                        },
                        value: {
                            fontFamily: 'Inter',
                            fontSize: theme.fontSize,
                            color: theme.textColor,
                            width: 40,
                            align: 'right'
                        }
                    }
                },
                formatter: (name: string) => {
                    const item = this.currentData.find(d => d.name === name);
                    if (!item) return name;

                    const total = this.currentData.reduce((s, d) => s + d.value, 0);
                    const percent = ((item.value / total) * 100).toFixed(2);

                    const trimmed = name.length > 24 ? name.slice(0, 22) + '…' : name.padEnd(24, ' ');
                    return `{name|${trimmed}}{value| ${percent}%}`;
                }
            },
            series: [
                {
                    name: 'Company Share',
                    type: 'pie',
                    radius: ['47%', '72%'],
                    center: ['25%', '50%'],
                    itemStyle: { borderColor: theme.bgPrimary, borderWidth: 2 },
                    label: { show: false },
                    labelLine: { show: false },
                    data: this.currentData
                }
            ],
            graphic: [
                {
                    type: 'group',
                    left: '25%',
                    top: '50%',
                    bounding: 'raw',
                    children: [
                        {
                            type: 'text',
                            x: 0,
                            y: -20,
                            style: {
                                text: this.activeTab === 'market-share' ? '% of Total Supply:' : 'Total Value:',
                                font: `500 ${theme.fontSize} Inter`,
                                fill: theme.textColor,
                                textAlign: 'center'
                            }
                        },
                        {
                            type: 'text',
                            x: 0,
                            y: 0,
                            style: {
                                text: this.activeTab === 'market-share'
                                    ? `${((this.currentData.reduce((s, d) => s + d.value, 0) / this.currentData.reduce((s, d) => s + d.value, 0)) * 100).toFixed(2)}%`
                                    : this.total_current_value_usd,
                                font: '600 24px Inter',
                                fill: textContentColor,
                                textAlign: 'center'
                            }
                        }
                    ]
                }
            ]
        };

        this.currentChart = echarts.init(container, null, { backgroundColor: theme.bgPrimary });
        this.currentChart.setOption(option);
    }


    private changeSymbol(stockId: string, stock: any = null): void {
        changeSymbolForWidget(this.shadowRoot, this.config, stock, stockId, (stockId: any) => this.setupChart(stockId), (type: any, data: any) => this.emit(type, data));
    }

    public on(type: WidgetEventType, handler: WidgetEventHandler): void {
        if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
        this.eventHandlers.get(type)!.push(handler);
    }
    public off(type: WidgetEventType, handler: WidgetEventHandler): void {
        const list = this.eventHandlers.get(type);
        if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
    }
    public setSymbol(symbol: string): void {
        this.changeSymbol(symbol);
        const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
        if (dropdown) dropdown.value = symbol;
    }
    public async getWidgetImage(): Promise<string> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        return await getWidgetDataURI(widget);
    }
  
    public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
        if (!this.shadowRoot) return;
      
        this.config.theme = toggleWidgetTheme(
            this.shadowRoot,
            theme
        ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
      
        this.updateDisplay();
    }
    public destroy(): void {
        if (this.widgetResizeObserver) { this.widgetResizeObserver.disconnect(); this.widgetResizeObserver = null; }
        this.eventHandlers.clear(); this.shadowRoot.innerHTML = '';
    }
    private emit(type: WidgetEventType, data: any): void {
        const handlers = this.eventHandlers.get(type);
        if (handlers) { const evt: WidgetEvent = { type, data }; handlers.forEach(h => h(evt)); }
    }

  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M15.15 20.3C12.3125 20.3 10 17.9875 10 15.15C10 12.3125 12.3125 10 15.15 10C17.9875 10 20.3 12.3125 20.3 15.15C20.3 17.9875 18 20.3 15.15 20.3ZM15.15 11.8625C13.3375 11.8625 11.875 13.3375 11.875 15.1375C11.875 16.9375 13.35 18.4125 15.15 18.4125C16.95 18.4125 18.425 16.9375 18.425 15.1375C18.425 13.3375 16.9625 11.8625 15.15 11.8625Z" fill="currentColor"/> <path d="M15 28.4375C7.5875 28.4375 1.5625 22.4125 1.5625 15C1.5625 7.5875 7.5875 1.5625 15 1.5625C22.4125 1.5625 28.4375 7.5875 28.4375 15C28.4375 22.4125 22.4125 28.4375 15 28.4375ZM15 3.4375C8.625 3.4375 3.4375 8.625 3.4375 15C3.4375 21.375 8.625 26.5625 15 26.5625C21.375 26.5625 26.5625 21.375 26.5625 15C26.5625 8.625 21.375 3.4375 15 3.4375Z" fill="currentColor"/> <path d="M15.0625 22C14.5158 22 14.0625 21.5192 14.0625 20.9394V9.06061C14.0625 8.48081 14.5158 8 15.0625 8C15.6092 8 16.0625 8.48081 16.0625 9.06061V20.9394C16.0625 21.5192 15.6092 22 15.0625 22Z" fill="currentColor"/> </svg>`;
  }

}

declare global { interface Window { TraderverseCryptoCompany: typeof TraderverseCryptoCompany; } }
if (typeof window !== 'undefined') window.TraderverseCryptoCompany = TraderverseCryptoCompany;